import { useState, useEffect } from 'react';
import { Info, AlertCircle, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditLifecycleMode } from '@/types/billing';
import { NotificationChannel, type CreditManagementApiResponse } from '@/services/billing/credit-management.service';
import {
  useCreditManagementSettings,
  useCreateCreditManagementSettings,
  useUpdateCreditManagementSettings,
  usePreviewCreditImpact,
  useShortestBillingCycle
} from '@/hooks/billing';
import { toast } from 'sonner';

interface CreditManagementSettingsProps {
  productId: string;
  shortestCycleName?: string;
  onSave?: (_settings: { productId: string; lifecycleMode: CreditLifecycleMode; expiryBillingCycles: number }) => void;
}

export function CreditManagementSettings({ 
  productId, 
  shortestCycleName,
  onSave 
}: CreditManagementSettingsProps) {
  const { t } = useTranslation();
  
  // API hooks
  const { 
    data: creditSettings, 
    isLoading, 
    error 
  } = useCreditManagementSettings(productId);
  
  const { data: shortestCycle } = useShortestBillingCycle(productId);
  const createSettingsMutation = useCreateCreditManagementSettings();
  const updateSettingsMutation = useUpdateCreditManagementSettings();
  const previewImpactMutation = usePreviewCreditImpact();
  
  // Local state
  const [lifecycleMode, setLifecycleMode] = useState<CreditLifecycleMode>('reset');
  const [expiryBillingCycles, setExpiryBillingCycles] = useState('6');
  const [hasChanges, setHasChanges] = useState(false);
  const [impactPreview, setImpactPreview] = useState<{affectedCustomers?: number; totalCreditsAffected?: number} | null>(null);
  const [lastSaveTimestamp, setLastSaveTimestamp] = useState<number>(0);
  
  // Use shortest cycle name from API or prop
  const cycleName = shortestCycle?.name || shortestCycleName || 'monthly';
  
  // Helper function to safely extract accumulation settings values
  const getAccumulationExpiryValue = (accumSettings: unknown): number => {
    if (!accumSettings || typeof accumSettings !== 'object') return 6;
    
    const settings = accumSettings as Record<string, unknown>;
    
    // Handle kebab-case properties
    if (typeof settings['expiry-billing-cycles'] === 'number') {
      return settings['expiry-billing-cycles'];
    }
    
    // Handle snake_case properties  
    if (typeof settings['expiry_billing_cycles'] === 'number') {
      return settings['expiry_billing_cycles'];
    }
    
    return 6;
  };
  
  // Sync API data with local state
  useEffect(() => {
    if (creditSettings) {
      // Only update from API data if we haven't saved recently (within 5 seconds)
      const timeSinceLastSave = Date.now() - lastSaveTimestamp;
      const recentlySaved = timeSinceLastSave < 5000;
      
      if (!recentlySaved) {
        // Handle both kebab-case and snake_case property names
        const settings = creditSettings as CreditManagementApiResponse;
        const lifecycleMode = settings['lifecycle-mode'] || settings.lifecycle_mode || 'reset';
        setLifecycleMode(lifecycleMode);
        
        // For accumulate mode, get expiry from accumulation_settings, otherwise use expiry_cycles
        const accumSettings = settings['accumulation-settings'] || settings.accumulation_settings;
        const expiryValue = getAccumulationExpiryValue(accumSettings) || settings.expiry_cycles || 6;
        setExpiryBillingCycles(expiryValue.toString());
        setHasChanges(false);
      }
    }
  }, [creditSettings, lastSaveTimestamp]);
  
  // Handle API errors (but not 404 which is handled as null data)
  useEffect(() => {
    if (error) {
      toast.error(t('errors.failedToLoadCreditSettings'));
    }
  }, [error, t]);


  const handleLifecycleModeChange = (value: CreditLifecycleMode) => {
    setLifecycleMode(value);
    setHasChanges(true);
    
    // Preview impact when changing modes
    previewImpactMutation.mutate({
      productId,
      data: {
        lifecycle_mode: value,
        ...(value === 'accumulate' && {
          accumulation_settings: {
            expiry_billing_cycles: parseInt(expiryBillingCycles),
            expiry_mode: 'fifo' as const,
            max_accumulation_months: parseInt(expiryBillingCycles)
          }
        })
      }
    }, {
      onSuccess: (data) => {
        const impactData = data as { affected_customers?: number; total_credits?: number };
        setImpactPreview({
          affectedCustomers: impactData.affected_customers || 0,
          totalCreditsAffected: impactData.total_credits || 0
        });
      }
    });
  };
  
  const handleExpiryChange = (value: string) => {
    setExpiryBillingCycles(value);
    setHasChanges(true);
  };
  
  const handleSave = async () => {
    const settingsData = {
      lifecycle_mode: lifecycleMode,
      ...(lifecycleMode === 'accumulate' && {
        accumulation_settings: {
          expiry_billing_cycles: parseInt(expiryBillingCycles),
          expiry_mode: 'fifo' as const,
          max_accumulation_months: parseInt(expiryBillingCycles)
        }
      }),
      usage_tracking_method: 'api_calls' as const,
      auto_topup: { enabled: false },
      notification_channels: ['email'] as NotificationChannel[]
    };
    
    
    try {
      // If creditSettings is null, it means no settings exist, so create them
      if (creditSettings === null) {
        const createdSettings = await createSettingsMutation.mutateAsync({
          productId,
          data: settingsData
        });
        
        // Update local state with the response to keep UI in sync
        if (createdSettings) {
          const settings = createdSettings as CreditManagementApiResponse;
          const lifecycleMode = settings['lifecycle-mode'] || settings.lifecycle_mode || 'reset';
          setLifecycleMode(lifecycleMode);
          
          const accumSettings = settings['accumulation-settings'] || settings.accumulation_settings;
          const expiryValue = getAccumulationExpiryValue(accumSettings) || settings.expiry_cycles || 6;
          setExpiryBillingCycles(expiryValue.toString());
          setLastSaveTimestamp(Date.now()); // Mark when we last saved
        }
        
        toast.success(t('products.creditSettingsCreated'));
        return;
      }

      // Try UPDATE for existing settings
      try {
        const updatedSettings = await updateSettingsMutation.mutateAsync({
          productId,
          data: settingsData
        });
        
        // Update local state with the response to keep UI in sync
        if (updatedSettings) {
          const settings = updatedSettings as CreditManagementApiResponse;
          const lifecycleMode = settings['lifecycle-mode'] || settings.lifecycle_mode || 'reset';
          setLifecycleMode(lifecycleMode);
          
          const accumSettings = settings['accumulation-settings'] || settings.accumulation_settings;
          const expiryValue = getAccumulationExpiryValue(accumSettings) || settings.expiry_cycles || 6;
          setExpiryBillingCycles(expiryValue.toString());
          setLastSaveTimestamp(Date.now()); // Mark when we last saved
        }
        
        toast.success(t('products.creditSettingsUpdated'));
      } catch (updateError: unknown) {
        // If update fails because settings don't exist, try creating instead
        const error = updateError as { 
          response?: { status?: number; data?: { errors?: Array<{ status?: string }> } }; 
          message?: string; 
        };
        if (error?.response?.status === 404 || 
            error?.message?.includes('not found') ||
            error?.response?.data?.errors?.[0]?.status === '404') {
          const createdSettings = await createSettingsMutation.mutateAsync({
            productId,
            data: settingsData
          });
          
          // Update local state with the response to keep UI in sync
          if (createdSettings) {
            const settings = createdSettings as CreditManagementApiResponse;
            const lifecycleMode = settings['lifecycle-mode'] || settings.lifecycle_mode || 'reset';
            setLifecycleMode(lifecycleMode);
            
            const accumSettings = settings['accumulation-settings'] || settings.accumulation_settings;
            const expiryValue = getAccumulationExpiryValue(accumSettings) || settings.expiry_cycles || 6;
            setExpiryBillingCycles(expiryValue.toString());
            setLastSaveTimestamp(Date.now()); // Mark when we last saved
          }
          
          toast.success(t('products.creditSettingsCreated'));
        } else {
          throw updateError;
        }
      }
      
      setHasChanges(false);
      
      if (onSave) {
        onSave({
          productId,
          lifecycleMode: lifecycleMode,
          expiryBillingCycles: parseInt(expiryBillingCycles)
        });
      }
    } catch (_error) {
      toast.error(t('errors.failedToSaveCreditSettings'));
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('products.creditManagementSettings')}</CardTitle>
        <CardDescription>
          {t('products.configureCreditSettings')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Credit Lifecycle */}
        <div className="space-y-4">
          <Label className="text-base">{t('products.creditLifecycle')}</Label>
          <RadioGroup value={lifecycleMode} onValueChange={handleLifecycleModeChange}>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-4 border rounded-lg">
                <RadioGroupItem value="reset" id="reset" className="mt-1" />
                <div className="space-y-1 flex-1">
                  <Label htmlFor="reset" className="font-medium cursor-pointer">
                    {t('products.creditsReset')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('products.creditsResetDescription', { cycle: cycleName })}
                  </p>
                  <Alert className="mt-2">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      {t('products.creditsResetInfo', { cycle: cycleName })}
                    </AlertDescription>
                  </Alert>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 border rounded-lg">
                <RadioGroupItem value="accumulate" id="accumulate" className="mt-1" />
                <div className="space-y-3 flex-1">
                  <div>
                    <Label htmlFor="accumulate" className="font-medium cursor-pointer">
                      {t('products.creditsAccumulate')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('products.creditsAccumulateDescription')}
                    </p>
                  </div>
                  
                  {lifecycleMode === 'accumulate' && (
                    <div className="ml-6 space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="expiry-cycles" className="text-sm">
                          {t('products.creditsExpireAfter')}
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="expiry-cycles"
                            type="number"
                            min="1"
                            className="w-24"
                            value={expiryBillingCycles}
                            onChange={(e) => handleExpiryChange(e.target.value)}
                          />
                          <span className="text-sm text-muted-foreground">
                            {t('products.billingCyclesText', { cycle: cycleName })}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('products.fifoInfo')}
                        </p>
                      </div>
                      
                      <Alert variant="default">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>{t('products.accountingImpact')}:</strong> {t('products.accumulatedCreditsInfo')}
                        </AlertDescription>
                      </Alert>
                      
                      {/* Impact Preview */}
                      {previewImpactMutation.isPending && (
                        <Alert>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <AlertDescription>
                            {t('products.calculatingImpact')}
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      {impactPreview && (
                        <Alert>
                          <Info className="h-4 w-4" />
                          <AlertDescription>
                            <strong>{t('products.estimatedImpact')}:</strong>
                            <br />
                            {t('products.affectedCustomers')}: {impactPreview.affectedCustomers || 0}
                            <br />
                            {t('products.totalCreditsAffected')}: {impactPreview.totalCreditsAffected || 0}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>
        
        {/* Save Controls */}
        {hasChanges && (
          <div className="flex items-center justify-between pt-4 border-t">
            <Alert className="flex-1 mr-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                {t('products.unsavedChanges')}
              </AlertDescription>
            </Alert>
            <Button 
              onClick={handleSave}
              disabled={createSettingsMutation.isPending || updateSettingsMutation.isPending}
            >
              {(createSettingsMutation.isPending || updateSettingsMutation.isPending) && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              <Save className="h-4 w-4 mr-2" />
              {t('common.save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
